import { type ReactNode } from "react";
import { ChevronDown } from "@langfuse/icons";
import { cn } from "@/src/utils/tailwind";

export function SessionTimelineCollapsibleRow({
  label,
  labelActionName,
  labelTitle,
  searchableLabel,
  labelTrailingContent,
  icon,
  isExpanded,
  onExpandedChange,
  onOpenObservation,
  trailingContent,
  showHoverDivider = true,
  children,
}: {
  label: string;
  labelActionName?: string;
  labelTitle?: string;
  searchableLabel?: boolean;
  labelTrailingContent?: ReactNode;
  icon?: ReactNode;
  onOpenObservation?: () => void;
  trailingContent?: ReactNode;
  showHoverDivider?: boolean;
  children?: ReactNode;
} & (
  | {
      isExpanded: boolean;
      onExpandedChange: (isExpanded: boolean) => void;
    }
  | { isExpanded?: never; onExpandedChange?: never }
)) {
  return (
    <section
      className={cn(
        "flex w-full scroll-mt-16 flex-col py-1",
        isExpanded && "gap-2",
      )}
      data-expanded={isExpanded}
    >
      <div
        className="group/collapsible-row flex w-full min-w-0 items-center gap-0.5"
        data-expanded={isExpanded}
      >
        {onExpandedChange || onOpenObservation ? (
          <button
            type="button"
            onClick={
              onOpenObservation ?? (() => onExpandedChange?.(!isExpanded))
            }
            className="flex min-w-0 items-center gap-2 rounded-sm text-left focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            aria-expanded={onOpenObservation ? undefined : isExpanded}
            aria-label={labelActionName}
          >
            {icon && (
              <span className="bg-background relative z-[1] flex shrink-0 rounded-full">
                {icon}
              </span>
            )}
            <span
              className="min-w-0 truncate text-xs font-normal hover:underline"
              title={labelTitle ?? label}
              data-session-search-content={searchableLabel || undefined}
            >
              {label}
            </span>
          </button>
        ) : (
          <span
            className="text-muted-foreground min-w-0 truncate text-xs"
            title={labelTitle ?? label}
          >
            {label}
          </span>
        )}
        {labelTrailingContent}
        {onExpandedChange && (
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground shrink-0 rounded-sm p-0.5 transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? "Collapse" : "Expand"} ${label}`}
            onClick={() => onExpandedChange(!isExpanded)}
          >
            <ChevronDown
              size="sm"
              __rawClassName={cn(
                "transition-transform",
                !isExpanded && "-rotate-90",
              )}
              aria-hidden="true"
            />
          </button>
        )}
        <div
          className={cn(
            "mx-3 min-w-0 flex-1",
            showHoverDivider &&
              "border-border invisible border-t border-dashed group-hover/collapsible-row:visible group-has-[:focus-visible]/collapsible-row:visible",
          )}
          aria-hidden="true"
        />
        <span className="flex shrink-0 items-center gap-2">
          {trailingContent}
        </span>
      </div>
      {isExpanded && <div className="min-w-0">{children}</div>}
    </section>
  );
}
